import React, { ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Progress,
  Toast,
  ToastAction,
  ToastClose,
  ToastDescription,
  ToastProvider as RadixToastProvider,
  ToastTitle,
  ToastViewport,
} from '@roger/ui-components';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const { toasts, removeToast, cancelUpload } = useStore(
    useShallow(state => ({
      toasts: state.toasts,
      removeToast: state.removeToast,
      cancelUpload: state.cancelUpload,
    }))
  );

  return (
    <RadixToastProvider>
      {children}
      {toasts.map(toast => {
        // A loading toast (or one with duration:null) persists until it's
        // updated or dismissed. Radix runs its OWN auto-close timer independent
        // of the store, so persistence must be enforced here too — passing
        // Infinity disables Radix's timer (react-toast treats Infinity as
        // "never auto-close"). Everything else keeps its store-side duration.
        const persistent = toast.loading === true || toast.duration === null;
        const radixDuration = persistent
          ? Infinity
          : (toast.duration ?? undefined);

        // Progress bar and Cancel are gated on `loading`, NOT on the presence of
        // progress/cancellable. This closes two holes: a stuck partial bar
        // clinging to a resolved success toast, and a live Cancel button on the
        // green toast when the terminal progress event never set cancellable:false.
        const showProgress = toast.loading === true && toast.progress != null;
        const showCancel = toast.loading === true && toast.cancellable === true;

        return (
          <Toast
            key={toast.id}
            variant={toast.variant}
            duration={radixDuration}
            onOpenChange={open => {
              // NOTE: X-dismissing a loading toast removes it without aborting
              // the underlying upload (no cancelUpload call) — the upload keeps
              // running in the background. Pre-existing behavior, not a Tier-0
              // regression; tracked with the cancel work in EMR-542.
              if (!open) removeToast(toast.id);
            }}
          >
            <div className='flex w-full items-center gap-3'>
              {toast.loading ? (
                <Loader2
                  className='h-4 w-4 shrink-0 animate-spin'
                  aria-hidden='true'
                />
              ) : null}
              <div className='grid flex-1 gap-1'>
                <ToastTitle>{toast.title}</ToastTitle>
                {toast.description && (
                  <ToastDescription className='whitespace-pre-line'>
                    {toast.description}
                  </ToastDescription>
                )}
                {showProgress ? (
                  <Progress
                    className='mt-1'
                    value={Math.round((toast.progress ?? 0) * 100)}
                  />
                ) : null}
              </div>
              {showCancel ? (
                <ToastAction
                  altText='Cancel upload'
                  onClick={() => cancelUpload(toast.id)}
                >
                  Cancel
                </ToastAction>
              ) : null}
              {toast.linkAction ? (
                // ToastProvider mounts ABOVE the Router, so useNavigate isn't
                // available here — a plain anchor deep-links the document.
                <ToastAction altText={toast.linkAction.altText} asChild>
                  <a
                    href={toast.linkAction.href}
                    {...(toast.linkAction.newTab
                      ? { target: '_blank', rel: 'noopener noreferrer' }
                      : {})}
                  >
                    {toast.linkAction.label}
                  </a>
                </ToastAction>
              ) : null}
            </div>
            <ToastClose />
          </Toast>
        );
      })}
      <ToastViewport />
    </RadixToastProvider>
  );
};
